import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@renderer/chat/providers'
import { useUser } from '@renderer/chat/store'
import { getChatApiRoute } from '@shared/utils/ChatBackendUtils'

export interface SwitchableOrganization {
  organizationId: string
  name: string
  role: string | null
  isPersonal: boolean
}

export interface OrganizationsData {
  currentOrganizationId: string | null
  organizations: Array<SwitchableOrganization>
  /**
   * Whether the org model is live for this user. Distinct from `organizations.length > 0`: a flag-on
   * user can legitimately have an empty list (not provisioned yet, or every org deactivated), and
   * callers that offer to create an organization must not mistake that for the flag being off.
   */
  orgModelEnabled: boolean
}

interface OrganizationsResponse {
  success: boolean
  data: OrganizationsData
}

export const ORGANIZATIONS_QUERY_KEY = 'auth-organizations'

export const useOrganizations = () => {
  const { getAuthToken } = useAuth()
  const user = useUser()
  // Scoped by the signed-in user id so an account switch can never serve the
  // previous account's organizations from cache; the id sits after the prefix
  // so [ORGANIZATIONS_QUERY_KEY] invalidations still match.
  const userId = user && !user.isAnonymous ? user.id : null

  return useQuery({
    queryKey: [ORGANIZATIONS_QUERY_KEY, userId],
    enabled: userId !== null,
    queryFn: async (): Promise<OrganizationsData> => {
      const token = await getAuthToken()
      const response = await fetch(getChatApiRoute('api/v1/auth/organizations'), {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      })

      // The endpoint 404s while the organization-model flag is off — treat as "no orgs".
      if (response.status === 404) {
        return { currentOrganizationId: null, organizations: [], orgModelEnabled: false }
      }

      if (!response.ok) {
        const body = await response.text().catch(() => '')
        throw new Error(`GET /auth/organizations failed: ${response.status} ${body}`)
      }

      const json = (await response.json()) as OrganizationsResponse
      return { ...json.data, orgModelEnabled: true }
    },
    staleTime: 1000 * 60 * 10,
  })
}
